import { usePageTitle } from "@/hooks/use-page-title";
import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Building2, FileText, Play, Clock, CheckCircle, XCircle, Loader2, RefreshCw, ChevronDown, ChevronRight, ExternalLink, FileCheck, Truck } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Link } from "wouter";
import type { Company, BatchInvoiceRun, WorkOrder, Carrier, DotProfile } from "@shared/schema";

interface CompanyWithUnbilledTotal extends Company {
  unbilledTotal: string;
  unbilledCount: number;
}

interface WorkOrderWithRelations extends WorkOrder {
  company?: (Company & { dotProfile?: DotProfile | null }) | null;
  carrier?: Carrier | null;
}

interface ColumnConfig {
  id: string;
  label: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
}

const COLUMN_CONFIG: ColumnConfig[] = [
  { id: 'id', label: 'ID', defaultWidth: 80, minWidth: 60, maxWidth: 120 },
  { id: 'company', label: 'Company', defaultWidth: 180, minWidth: 120, maxWidth: 300 },
  { id: 'interval', label: 'Interval', defaultWidth: 100, minWidth: 80, maxWidth: 150 },
  { id: 'period', label: 'Period', defaultWidth: 180, minWidth: 140, maxWidth: 280 },
  { id: 'status', label: 'Status', defaultWidth: 120, minWidth: 90, maxWidth: 180 },
  { id: 'created', label: 'Created', defaultWidth: 120, minWidth: 90, maxWidth: 180 },
  { id: 'stripeInvoice', label: 'Stripe Invoice', defaultWidth: 160, minWidth: 120, maxWidth: 250 },
];

const STORAGE_KEY = 'batch-invoices-column-widths';

function getStoredColumnWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return {};
}

function saveColumnWidths(widths: Record<string, number>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
  } catch {}
}

function formatDate(date: string | Date | null): string {
  if (!date) return "-";
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatInterval(interval: string | null): string {
  if (!interval) return "-";
  return interval.charAt(0).toUpperCase() + interval.slice(1);
}

function getInvoiceRunStatusBadge(status: string) {
  switch (status) {
    case "completed":
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" /> Completed</Badge>;
    case "processing":
      return <Badge className="bg-blue-100 text-blue-800"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Processing</Badge>;
    case "failed":
      return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" /> Failed</Badge>;
    case "pending":
    default:
      return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
  }
}

function getWorkOrderStatusBadge(status: string) {
  switch (status) {
    case "completed":
      return <Badge className="bg-green-100 text-green-800 text-xs">Completed</Badge>;
    case "in_progress":
      return <Badge className="bg-blue-100 text-blue-800 text-xs">In Progress</Badge>;
    case "on_hold":
      return <Badge className="bg-orange-100 text-orange-800 text-xs">On Hold</Badge>;
    case "cancelled":
      return <Badge className="bg-red-100 text-red-800 text-xs">Cancelled</Badge>;
    case "pending":
    default:
      return <Badge className="bg-yellow-100 text-yellow-800 text-xs">Pending</Badge>;
  }
}

interface CompanyWorkOrdersPanelProps {
  company: Company;
  onSelectionChange?: (selectedIds: number[]) => void;
  onTriggerBatch?: (company: Company, workOrderIds: number[]) => void;
}

function CompanyWorkOrdersPanel({ company, onSelectionChange, onTriggerBatch }: CompanyWorkOrdersPanelProps) {
  const { data: workOrders = [], isLoading } = useQuery<(WorkOrderWithRelations & { totalAmount: string })[]>({
    queryKey: ['/api/batch-billing/companies', company.id, 'unbilled-work-orders'],
    queryFn: async () => {
      const res = await fetch(`/api/batch-billing/companies/${company.id}/unbilled-work-orders`);
      if (!res.ok) throw new Error('Failed to fetch work orders');
      return res.json();
    },
  });

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (workOrders.length > 0) {
      const ids = new Set(workOrders.map(wo => wo.id));
      setSelectedIds(ids);
      onSelectionChange?.(Array.from(ids));
    }
  }, [workOrders]);

  const allSelected = workOrders.length > 0 && selectedIds.size === workOrders.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < workOrders.length;

  const toggleSelectAll = () => {
    let newIds: Set<number>;
    if (allSelected) {
      newIds = new Set();
    } else {
      newIds = new Set(workOrders.map(wo => wo.id));
    }
    setSelectedIds(newIds);
    onSelectionChange?.(Array.from(newIds));
  };

  const toggleWorkOrder = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      onSelectionChange?.(Array.from(next));
      return next;
    });
  };
  
  const handleCreateBatchInvoice = () => {
    if (selectedIds.size > 0) {
      onTriggerBatch?.(company, Array.from(selectedIds));
    }
  };

  const selectedTotal = workOrders
    .filter(wo => selectedIds.has(wo.id))
    .reduce((sum, wo) => sum + (parseFloat(wo.totalAmount?.toString() || '0') || 0), 0);

  if (isLoading) {
    return (
      <div className="py-4 pl-12 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading work orders...
      </div>
    );
  }

  if (workOrders.length === 0) {
    return (
      <div className="py-4 pl-12 text-muted-foreground text-sm">
        No unbilled work orders found for this company.
      </div>
    );
  }

  return (
    <div className="bg-muted/30 border-t">
      <div className="px-6 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id={`select-all-${company.id}`}
                checked={allSelected}
                onCheckedChange={toggleSelectAll}
                className="data-[state=indeterminate]:bg-primary"
                data-state={someSelected ? "indeterminate" : allSelected ? "checked" : "unchecked"}
                data-testid={`checkbox-select-all-${company.id}`}
              />
              <label htmlFor={`select-all-${company.id}`} className="text-sm cursor-pointer">
                {allSelected ? "Deselect All" : "Select All"}
              </label>
            </div>
            <h4 className="text-sm font-medium flex items-center gap-2">
              <FileCheck className="w-4 h-4" />
              Unbilled Work Orders ({selectedIds.size} of {workOrders.length} selected)
            </h4>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="text-sm">
              Selected Total: ${selectedTotal.toFixed(2)}
            </Badge>
            <Button
              size="sm"
              onClick={handleCreateBatchInvoice}
              disabled={selectedIds.size === 0}
              data-testid={`button-create-batch-${company.id}`}
            >
              <FileText className="w-4 h-4 mr-2" />
              Create Batch Invoice ({selectedIds.size})
            </Button>
          </div>
        </div>
        
        <div className="bg-muted/50 rounded-t-lg border border-b-0 px-3 py-2 text-xs font-medium text-muted-foreground grid grid-cols-12 gap-2 items-center">
          <div className="col-span-1"></div>
          <div className="col-span-2">Work Order</div>
          <div className="col-span-2">Service</div>
          <div className="col-span-2">DOT / Docket</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2">Last Updated</div>
          <div className="col-span-2 text-right">Amount</div>
        </div>
        
        <div className="border rounded-b-lg divide-y">
          {workOrders.map((workOrder) => (
            <div 
              key={workOrder.id} 
              className={`bg-background px-3 py-2 grid grid-cols-12 gap-2 items-center hover:bg-muted/30 transition-colors ${
                selectedIds.has(workOrder.id) ? '' : 'opacity-50'
              }`}
              data-testid={`unbilled-wo-${workOrder.id}`}
            >
              <div className="col-span-1">
                <Checkbox
                  checked={selectedIds.has(workOrder.id)}
                  onCheckedChange={() => toggleWorkOrder(workOrder.id)}
                  data-testid={`checkbox-wo-${workOrder.id}`}
                />
              </div>
              <div className="col-span-2">
                <Link 
                  href={`/work-orders/${workOrder.id}`}
                  className="font-medium text-primary hover:underline flex items-center gap-1"
                  data-testid={`link-wo-${workOrder.id}`}
                >
                  {workOrder.workOrderNumber}
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
              <div className="col-span-2 text-sm text-muted-foreground">
                {workOrder.serviceType || '-'}
              </div>
              <div className="col-span-2 text-sm">
                {workOrder.carrier ? (
                  <div className="flex flex-col">
                    <span className="text-muted-foreground">
                      DOT: {workOrder.company?.dotProfile?.dotNumber || '-'}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {workOrder.carrier.docketPrefix || 'MC'}: {workOrder.carrier.docketNumber || '-'}
                    </span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </div>
              <div className="col-span-1">
                {getWorkOrderStatusBadge(workOrder.status)}
              </div>
              <div className="col-span-2 text-sm text-muted-foreground">
                {formatDate(workOrder.updatedAt)}
              </div>
              <div className="col-span-2 text-right font-medium">
                ${parseFloat(workOrder.totalAmount?.toString() || '0').toFixed(2)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function BatchInvoicesPage() {
  usePageTitle("Batch Invoices");
  const [activeTab, setActiveTab] = useState("companies");
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedWorkOrderIds, setSelectedWorkOrderIds] = useState<number[]>([]);
  const [expandedCompanies, setExpandedCompanies] = useState<Set<number>>(new Set());
  
  const { toast } = useToast();

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const stored = getStoredColumnWidths();
    const defaults: Record<string, number> = {};
    COLUMN_CONFIG.forEach(col => {
      defaults[col.id] = stored[col.id] || col.defaultWidth;
    });
    return defaults;
  });

  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);

  const handleResizeStart = useCallback((columnId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth = columnWidths[columnId] || COLUMN_CONFIG.find(c => c.id === columnId)?.defaultWidth || 100;
    resizingRef.current = { columnId, startX: e.clientX, startWidth };
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizingRef.current) return;
      const config = COLUMN_CONFIG.find(c => c.id === resizingRef.current!.columnId);
      if (!config) return;
      
      const diff = moveEvent.clientX - resizingRef.current.startX;
      const newWidth = Math.max(
        config.minWidth,
        Math.min(config.maxWidth, resizingRef.current.startWidth + diff)
      );
      
      setColumnWidths(prev => ({
        ...prev,
        [resizingRef.current!.columnId]: newWidth
      }));
    };
    
    const handleMouseUp = () => {
      if (resizingRef.current) {
        setColumnWidths(prev => {
          saveColumnWidths(prev);
          return prev;
        });
      }
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  const getColumnWidth = useCallback((columnId: string) => {
    return columnWidths[columnId] || COLUMN_CONFIG.find(c => c.id === columnId)?.defaultWidth || 100;
  }, [columnWidths]);
  
  const toggleExpanded = (companyId: number) => {
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  };

  const { data: batchCompanies = [], isLoading: loadingCompanies } = useQuery<CompanyWithUnbilledTotal[]>({
    queryKey: ['/api/batch-billing/companies'],
    refetchOnMount: "always",
  });

  const { data: batchRuns = [], isLoading: loadingRuns } = useQuery<BatchInvoiceRun[]>({
    queryKey: ['/api/batch-invoice-runs'],
    refetchOnMount: "always",
  });

  const triggerBatchMutation = useMutation({
    mutationFn: async (data: { companyId: number; intervalType: string; periodStart: string; periodEnd: string; workOrderIds: number[] }) => {
      const response = await apiRequest('POST', '/api/batch-invoice-runs', data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/batch-invoice-runs'] });
      queryClient.invalidateQueries({ queryKey: ['/api/invoices'] });
      queryClient.invalidateQueries({ queryKey: ['/api/batch-billing/companies'] });
      setTriggerModalOpen(false);
      setSelectedCompany(null);
      setSelectedWorkOrderIds([]);
      toast({
        title: "Batch invoice created",
        description: `Created batch invoice with ${selectedWorkOrderIds.length} work orders.`,
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create batch invoice",
        variant: "destructive",
      });
    },
  });

  const handleTriggerBatch = (company: Company, workOrderIds: number[]) => {
    setSelectedCompany(company);
    setSelectedWorkOrderIds(workOrderIds);
    setTriggerModalOpen(true);
  };

  const confirmTriggerBatch = () => {
    if (!selectedCompany || selectedWorkOrderIds.length === 0) return;
    
    const today = new Date();
    const periodEnd = today.toISOString().split('T')[0];
    
    let periodStart: string;
    const interval = selectedCompany.batchInterval || 'monthly';
    
    switch (interval) {
      case 'weekly':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        periodStart = weekAgo.toISOString().split('T')[0];
        break;
      case 'biweekly':
        const twoWeeksAgo = new Date(today);
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
        periodStart = twoWeeksAgo.toISOString().split('T')[0];
        break;
      case 'monthly':
      default:
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        periodStart = monthAgo.toISOString().split('T')[0];
        break;
    }

    triggerBatchMutation.mutate({
      companyId: selectedCompany.id,
      intervalType: interval,
      periodStart,
      periodEnd,
      workOrderIds: selectedWorkOrderIds,
    });
  };

  return (
    <ResponsiveLayout title="Batch Invoicing" subtitle="Manage batch billing and invoice runs" currentPage="batch-invoices">
      <div className="p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="companies" className="flex items-center gap-2" data-testid="tab-companies">
              <Building2 className="w-4 h-4" />
              Batch Billing Companies
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2" data-testid="tab-history">
              <FileText className="w-4 h-4" />
              Invoice Run History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="companies">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Companies with Batch Billing
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => queryClient.invalidateQueries({ queryKey: ['/api/batch-billing/companies'] })}
                    data-testid="button-refresh-companies"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingCompanies ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : batchCompanies.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No companies configured for batch billing.</p>
                    <p className="text-sm mt-2">To enable batch billing, edit a company and set the billing preference to "Batch".</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-[auto_1fr_120px_120px_130px_160px_100px] gap-4 px-4 py-2 text-sm font-medium text-muted-foreground border-b">
                      <div className="w-6"></div>
                      <div>Company</div>
                      <div>Batch Interval</div>
                      <div>Next Run</div>
                      <div className="text-right">Unbilled Total</div>
                      <div>Stripe Customer</div>
                      <div className="text-right">Actions</div>
                    </div>
                    {batchCompanies.map((company) => {
                      const isExpanded = expandedCompanies.has(company.id);
                      return (
                        <Collapsible 
                          key={company.id} 
                          open={isExpanded} 
                          onOpenChange={() => toggleExpanded(company.id)}
                        >
                          <div 
                            className="border rounded-lg overflow-hidden"
                            data-testid={`row-company-${company.id}`}
                          >
                            <CollapsibleTrigger asChild>
                              <div className="grid grid-cols-[auto_1fr_120px_120px_130px_160px_100px] gap-4 px-4 py-3 items-center cursor-pointer hover:bg-muted/50 transition-colors">
                                <div className="w-6 flex items-center justify-center">
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                  )}
                                </div>
                                <div className="font-medium">{company.name}</div>
                                <div>
                                  <Badge variant="outline">
                                    <Calendar className="w-3 h-3 mr-1" />
                                    {formatInterval(company.batchInterval)}
                                  </Badge>
                                </div>
                                <div className="text-sm">
                                  {company.nextBatchRunAt ? formatDate(company.nextBatchRunAt) : "Not scheduled"}
                                </div>
                                <div className="text-right">
                                  {company.unbilledCount > 0 ? (
                                    <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                                      ${parseFloat(company.unbilledTotal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground text-sm">$0.00</span>
                                  )}
                                </div>
                                <div>
                                  {company.stripeCustomerId ? (
                                    <Badge variant="secondary" className="text-xs">
                                      {company.stripeCustomerId.substring(0, 14)}...
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground text-sm">Not linked</span>
                                  )}
                                </div>
                                <div className="text-right text-muted-foreground text-sm" onClick={(e) => e.stopPropagation()}>
                                  {!isExpanded && <ChevronRight className="w-4 h-4 inline" />}
                                </div>
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <CompanyWorkOrdersPanel 
                                company={company} 
                                onTriggerBatch={handleTriggerBatch}
                              />
                            </CollapsibleContent>
                          </div>
                        </Collapsible>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card className="overflow-hidden">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Batch Invoice Run History
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => queryClient.invalidateQueries({ queryKey: ['/api/batch-invoice-runs'] })}
                    data-testid="button-refresh-history"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loadingRuns ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : batchRuns.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No batch invoice runs yet.</p>
                    <p className="text-sm mt-2">Trigger a batch invoice from the Companies tab to get started.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table className="w-full" style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        {COLUMN_CONFIG.map(col => (
                          <col key={col.id} style={{ width: `${getColumnWidth(col.id)}px` }} />
                        ))}
                      </colgroup>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="relative group" style={{ width: getColumnWidth('id') }}>
                            <div className="pr-2">ID</div>
                            <div 
                              className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                              onMouseDown={(e) => handleResizeStart('id', e)}
                            />
                          </TableHead>
                          <TableHead className="relative group" style={{ width: getColumnWidth('company') }}>
                            <div className="pr-2">Company</div>
                            <div 
                              className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                              onMouseDown={(e) => handleResizeStart('company', e)}
                            />
                          </TableHead>
                          <TableHead className="relative group" style={{ width: getColumnWidth('interval') }}>
                            <div className="pr-2">Interval</div>
                            <div 
                              className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                              onMouseDown={(e) => handleResizeStart('interval', e)}
                            />
                          </TableHead>
                          <TableHead className="relative group" style={{ width: getColumnWidth('period') }}>
                            <div className="pr-2">Period</div>
                            <div 
                              className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                              onMouseDown={(e) => handleResizeStart('period', e)}
                            />
                          </TableHead>
                          <TableHead className="relative group" style={{ width: getColumnWidth('status') }}>
                            <div className="pr-2">Status</div>
                            <div 
                              className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                              onMouseDown={(e) => handleResizeStart('status', e)}
                            />
                          </TableHead>
                          <TableHead className="relative group" style={{ width: getColumnWidth('created') }}>
                            <div className="pr-2">Created</div>
                            <div 
                              className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                              onMouseDown={(e) => handleResizeStart('created', e)}
                            />
                          </TableHead>
                          <TableHead style={{ width: getColumnWidth('stripeInvoice') }}>Stripe Invoice</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {batchRuns.map((run) => {
                          const company = batchCompanies.find(c => c.id === run.companyId);
                          return (
                            <TableRow key={run.id} data-testid={`row-run-${run.id}`}>
                              <TableCell className="font-mono text-sm">#{run.id}</TableCell>
                              <TableCell>{company?.name || `Company #${run.companyId}`}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{formatInterval(run.intervalType)}</Badge>
                              </TableCell>
                              <TableCell className="text-sm">
                                {formatDate(run.periodStart)} - {formatDate(run.periodEnd)}
                              </TableCell>
                              <TableCell>{getInvoiceRunStatusBadge(run.status)}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {formatDate(run.createdAt)}
                              </TableCell>
                              <TableCell>
                                {run.stripeInvoiceId ? (
                                  <Badge variant="secondary" className="text-xs font-mono">
                                    {run.stripeInvoiceId.substring(0, 18)}...
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={triggerModalOpen} onOpenChange={setTriggerModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Batch Invoice</DialogTitle>
              <DialogDescription>
                You are about to create a batch invoice for {selectedCompany?.name} with {selectedWorkOrderIds.length} work order(s).
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                This will create a single invoice containing all selected work orders and send it to the customer via Stripe.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTriggerModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={confirmTriggerBatch} disabled={triggerBatchMutation.isPending}>
                {triggerBatchMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Create Invoice
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ResponsiveLayout>
  );
}

export default BatchInvoicesPage;
